<script setup>
import { computed, toRef } from 'vue';
import { useInlineSvg } from 'shared/composables/useInlineSvg';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    default: '',
  },
  imageClass: {
    type: String,
    default: '',
  },
  svgWrapperClass: {
    type: String,
    default: '',
  },
  width: {
    type: [Number, String],
    default: null,
  },
  height: {
    type: [Number, String],
    default: null,
  },
  maxWidth: {
    type: [Number, String],
    default: null,
  },
  maxHeight: {
    type: [Number, String],
    default: null,
  },
});

const { isSvg, svgMarkup, loadFailed } = useInlineSvg(toRef(props, 'src'));

const toCssSize = value => {
  if (value == null || value === '') return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

const svgWrapperStyle = computed(() => {
  const style = {
    width: toCssSize(props.width),
    height: toCssSize(props.height),
    maxWidth: toCssSize(props.maxWidth),
    maxHeight: toCssSize(props.maxHeight),
  };

  if (props.height && !props.width) {
    style.width = 'auto';
  }

  if (props.width && !props.height) {
    style.height = 'auto';
  }

  return style;
});

const hasExplicitSize = computed(
  () =>
    props.width != null ||
    props.height != null ||
    props.maxWidth != null ||
    props.maxHeight != null
);

const svgWrapperBaseClass = computed(() =>
  hasExplicitSize.value
    ? 'inline-flex shrink-0 items-center justify-center overflow-hidden'
    : 'block size-full min-h-0 min-w-0 overflow-hidden'
);

const svgInnerClass = computed(() => {
  if (props.height && !props.width) {
    return '[&_svg]:block [&_svg]:h-full [&_svg]:w-auto [&_svg]:max-w-full';
  }

  if (props.width && props.height) {
    return '[&_svg]:block [&_svg]:size-full';
  }

  return '[&_svg]:block [&_svg]:size-full';
});

const showInlineSvg = computed(
  () => props.src && isSvg.value && svgMarkup.value && !loadFailed.value
);

const showImage = computed(
  () => props.src && (!isSvg.value || loadFailed.value || !svgMarkup.value)
);
</script>

<template>
  <span
    v-if="showInlineSvg"
    v-dompurify-html="svgMarkup"
    :class="[svgWrapperBaseClass, svgInnerClass, svgWrapperClass]"
    :style="svgWrapperStyle"
    role="img"
    :aria-label="alt"
  />
  <img v-else-if="showImage" :src="src" :alt="alt" :class="imageClass" />
</template>
